// Het veld extra informatie op de opstartfiche (SPEC_calculatie 8, 9 en 10) en afwijkende eenheden (8.1).
//
// Het vrije tekstveld bevat regelmatig betaalbare posten ("drie kranen verlengen", "schouw los van de
// gevel 6 m2", "verplaatsing meer dan 100 km"). De tool herkent ze en stelt per herkenning een
// bevestigingsvraag voordat de post in de calculatie komt; de eenheidsprijs komt uit de prijslijst.
// Elke bevestiging wordt een regel: dezelfde formulering levert daarna geen vraag meer op (10.1), en
// de regels zijn beheerbaar (10.2, 10.3). Pure functies, gedeeld door server en scherm.
import { normalizeText } from './normalizer.js';
import { GEVEL_ACCESSOIRE_HERKENNING, gevelRol, type GevelConfig } from './gevel.js';
import { round2 } from './schijven.js';
import type { MatchedLine, MeerwerkLine, ParsedItem, PricelistItem } from './types.js';

/** Een post waaraan een herkenning gekoppeld kan worden: uit de gevellijst (code) of de prijslijst. */
export interface PostKandidaat {
  /** Gevellijst: de postcode (bv. "verlengen_kraan"); prijslijst: de id van de post. */
  code: string;
  naam: string;
  eenheid: string;
  prijs: number;
  /** Id van de prijslijstpost (gevellijst: de post in de gevelprijslijst als bekend). */
  plItemId: string | null;
  /** true = een post uit de gevellijst A/B (accessoires, schouwen). */
  gevel: boolean;
}

export type ExtraInfoSoort = 'post' | 'schouw_crepi';

export interface ExtraInfoVraag {
  /** Stabiel per fragment, zodat een antwoord op de juiste vraag landt. */
  id: string;
  fragment: string;
  /** Genormaliseerde formulering zonder getallen: de sleutel van een geleerde regel (10.1). */
  sleutel: string;
  soort: ExtraInfoSoort;
  post: PostKandidaat | null;
  /** Hoeveelheid die de tool veronderstelt; null = de tool vraagt ze (bv. aantal dagen). */
  hoeveelheid: number | null;
  eenheid: string;
  /** Wat de tool herkende, in mensentaal: "3 STUK Verlengen van een kraan aan 20,00 EUR". */
  uitleg: string;
  /** Id van de geleerde regel die deze vraag automatisch beantwoordt (10.4); null = vraag stellen. */
  regelId: string | null;
}

export interface ExtraInfoRegel {
  id: string;
  sleutel: string;
  beslissing: 'post' | 'negeren' | 'zelfde_getal';
  postCode: string | null;
  postNaam: string;
  eenheid: string;
  actief: boolean;
}

const GETALWOORDEN: Record<string, number> = {
  een: 1, eén: 1, één: 1, twee: 2, drie: 3, vier: 4, vijf: 5, zes: 6, zeven: 7, acht: 8, negen: 9, tien: 10, elf: 11, twaalf: 12,
};

function fmtEur(n: number): string {
  return `${n.toLocaleString('nl-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} EUR`;
}

function fmtGetal(n: number): string {
  return n.toLocaleString('nl-BE', { maximumFractionDigits: 2 });
}

/** Eenheid normaliseren: ST/STK/STUKS → STUK, M/MTR/METER → LM, M² → M2. */
export function normEenheid(e: string | null | undefined): string {
  const t = (e ?? '').trim().toUpperCase().replace('²', '2').replace(/\.$/, '');
  if (!t) return '';
  if (/^(ST|STK|STUK|STUKS|PCS|X)$/.test(t)) return 'STUK';
  if (/^(M|MTR|METER|LM|LOPENDE METER|M1)$/.test(t)) return 'LM';
  if (/^(M2|VIERKANTE METER)$/.test(t)) return 'M2';
  if (/^(FORF|FORFAIT|FF)$/.test(t)) return 'FORFAIT';
  return t;
}

/** Splitst het tekstveld in fragmenten: regels, opsommingstekens, zinnen. */
export function splitsFragmenten(tekst: string | null | undefined): string[] {
  return (tekst ?? '')
    .split(/\r?\n|;|•|·|(?<=[.!?])\s+(?=[A-ZÀ-Ö0-9])|\s+-\s+/)
    .map((f) => f.replace(/^[\s*\-–—•·>]+/, '').trim())
    .filter((f) => f.length >= 3);
}

/** Formulering zonder getallen en leestekens: "3 kranen verlengen" en "2 kranen verlengen" zijn dezelfde regel. */
export function sleutelVan(fragment: string): string {
  return normalizeText(fragment).replace(/\d+(?:[.,]\d+)?/g, '#').replace(/\s+/g, ' ').trim();
}

function hash(s: string): string {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return (h >>> 0).toString(36);
}

/** Leest een hoeveelheid en eenheid uit een fragment: "6 m2", "12 lm", "3 stuks", "drie kranen". */
export function leesHoeveelheid(fragment: string): { getal: number; eenheid: string | null } | null {
  const t = fragment.toLowerCase();
  const getal = (s: string) => parseFloat(s.replace(',', '.'));
  const m2 = /(\d+(?:[.,]\d+)?)\s*(m2|m²|vierkante\s*meter)/.exec(t);
  if (m2) return { getal: getal(m2[1]!), eenheid: 'M2' };
  const lm = /(\d+(?:[.,]\d+)?)\s*(lm|lopende\s*meter|meter|m)\b/.exec(t);
  if (lm) return { getal: getal(lm[1]!), eenheid: 'LM' };
  const st = /(\d+(?:[.,]\d+)?)\s*(stuks?|st\b|x\b)/.exec(t);
  if (st) return { getal: getal(st[1]!), eenheid: 'STUK' };
  const dg = /(\d+(?:[.,]\d+)?)\s*(dagen|dag)\b/.exec(t);
  if (dg) return { getal: getal(dg[1]!), eenheid: 'DAG' };
  const kaal = /(?:^|\s)(\d+(?:[.,]\d+)?)\s+[a-zà-ö]/.exec(t);
  if (kaal && !/\bkm\b/.test(t)) return { getal: getal(kaal[1]!), eenheid: null };
  for (const [w, n] of Object.entries(GETALWOORDEN)) {
    if (new RegExp(`(^|\\s)${w}\\s+[a-zà-ö]`).test(t) && w !== 'een') return { getal: n, eenheid: null };
  }
  return null;
}

/** De posten van de gevellijst (accessoires en schouwen) als kandidaten. */
export function gevelKandidaten(gevel: GevelConfig | null | undefined, plItemIdVoorCode?: (code: string) => string | null): PostKandidaat[] {
  return Object.entries(gevel?.accessoires ?? {}).map(([code, p]) => ({
    code, naam: p.naam, eenheid: p.eenheid, prijs: p.prijs, plItemId: plItemIdVoorCode?.(code) ?? null, gevel: true,
  }));
}

/** De posten van een gewone prijslijst als kandidaten (enkel betaalde posten). */
export function prijslijstKandidaten(items: PricelistItem[]): PostKandidaat[] {
  return items
    .filter((i) => !i.isNvt && i.naamArbeid !== 'NVT' && i.prijs > 0)
    .map((i) => ({ code: i.id, naam: i.naamArbeid || i.pdfNaam, eenheid: i.eenheid, prijs: i.prijs, plItemId: i.id, gevel: false }));
}

interface Herkenning {
  code: string;
  hoeveelheid: number | null;
  eenheid?: string;
  soort?: ExtraInfoSoort;
}

/** Herkenning op de gevellijst: de voorbeelden uit SPEC_calculatie 8 en de schouwen uit 9. */
function herkenGevel(fragment: string): Herkenning | null {
  const t = normalizeText(fragment);
  const q = leesHoeveelheid(fragment);
  if (/\bschouw/.test(t)) {
    const los = /\blos\b|losstaand|niet aaneensluitend|niet aan de gevel|op het dak|apart/.test(t);
    if (!los) return null; // Aaneensluitend: telt mee in de m2 van de gevel, geen aparte post (9).
    const m2 = q?.eenheid === 'M2' ? q.getal : null;
    if (m2 != null && m2 > 8) return { code: 'schouw_los_meer_dan_8m2', hoeveelheid: m2, eenheid: 'M2', soort: 'schouw_crepi' };
    const aantal = q && q.eenheid !== 'M2' ? q.getal : 1;
    return { code: m2 != null && m2 > 4 ? 'schouw_los_4_tot_8m2' : 'schouw_los_tot_4m2', hoeveelheid: aantal, eenheid: 'STUK' };
  }
  const km = /(\d{2,4})\s*km\b/.exec(t);
  if ((km && Number(km[1]) > 100) || /meer dan 100 ?km|verder dan 100 ?km|km vergoeding|kilometervergoeding/.test(t)) {
    return { code: 'km_vergoeding', hoeveelheid: q?.eenheid === 'DAG' ? q.getal : null, eenheid: 'DAG' };
  }
  if (/\bkra(a)?n(en)?\b/.test(t) && /verleng/.test(t)) return { code: 'verlengen_kraan', hoeveelheid: q?.getal ?? 1 };
  if (/verlichting|\blamp(en)?\b|buitenlamp|buitenverlichting/.test(t) && /verleng|verplaats|plaats/.test(t)) return { code: 'verlengen_verlichting', hoeveelheid: q?.getal ?? null };
  if (/hanggo(o)?t(en)?/.test(t) && /herplaats|afhalen|terugplaats|huidige|terug plaatsen/.test(t)) return { code: 'hanggoot', hoeveelheid: q?.eenheid === 'LM' ? q.getal : null, eenheid: 'LM' };
  if (/\bluifel/.test(t)) return { code: 'luifel', hoeveelheid: q?.getal ?? 1 };
  if (/deurdorpel/.test(t)) return { code: 'deurdorpels', hoeveelheid: q?.eenheid === 'LM' ? q.getal : q?.getal ?? null, eenheid: q?.eenheid === 'STUK' || (q && !q.eenheid) ? 'STUK' : 'LM' };
  const upper = fragment.toUpperCase();
  const code = GEVEL_ACCESSOIRE_HERKENNING.find((h) => h.patroon.test(upper))?.code;
  if (code) return { code, hoeveelheid: q?.getal ?? null, eenheid: q?.eenheid ?? undefined };
  return null;
}

/** Herkenning op een gewone prijslijst: de omschrijving van de post staat letterlijk in de tekst. */
function herkenPrijslijst(fragment: string, kandidaten: PostKandidaat[]): Herkenning | null {
  const t = ` ${normalizeText(fragment)} `;
  const treffers = kandidaten
    .map((k) => ({ k, n: normalizeText(k.naam) }))
    .filter(({ n }) => n.length >= 8 && n.includes(' ') && t.includes(` ${n} `))
    .sort((a, b) => b.n.length - a.n.length);
  if (!treffers.length) return null;
  const q = leesHoeveelheid(fragment);
  return { code: treffers[0]!.k.code, hoeveelheid: q?.getal ?? null, eenheid: q?.eenheid ?? undefined };
}

/**
 * Herkent posten in het veld extra informatie. Fragmenten met een geleerde regel "negeren" of die al als
 * post op de calculatie staan, vallen weg; een geleerde regel "post" beantwoordt de vraag automatisch.
 */
export function herkenExtraInformatie(input: {
  tekst: string | null | undefined;
  kandidaten: PostKandidaat[];
  regels?: ExtraInfoRegel[];
  /** Sleutels van fragmenten die al als post op de calculatie staan. */
  afgehandeld?: Set<string>;
}): ExtraInfoVraag[] {
  const regels = new Map((input.regels ?? []).filter((r) => r.actief).map((r) => [r.sleutel, r] as const));
  const perCode = new Map(input.kandidaten.map((k) => [k.code, k] as const));
  const gevel = input.kandidaten.some((k) => k.gevel);
  const out: ExtraInfoVraag[] = [];
  const gezien = new Set<string>();
  for (const fragment of splitsFragmenten(input.tekst)) {
    const sleutel = sleutelVan(fragment);
    if (!sleutel || gezien.has(sleutel) || input.afgehandeld?.has(sleutel)) continue;
    gezien.add(sleutel);
    const regel = regels.get(sleutel);
    if (regel?.beslissing === 'negeren') continue;
    const h = (gevel ? herkenGevel(fragment) : null) ?? herkenPrijslijst(fragment, input.kandidaten.filter((k) => !k.gevel));
    // Een geleerde regel mag een post verplaatst hebben (10.3): die post geldt, ook zonder herkenning.
    const code = regel?.beslissing === 'post' && regel.postCode ? regel.postCode : h?.code;
    const post = code ? perCode.get(code) ?? (regel?.postCode ? { code: regel.postCode, naam: regel.postNaam, eenheid: regel.eenheid, prijs: 0, plItemId: null, gevel } : null) : null;
    if (!post) continue;
    const soort: ExtraInfoSoort = h?.soort ?? (post.code === 'schouw_los_meer_dan_8m2' ? 'schouw_crepi' : 'post');
    const q = h?.hoeveelheid ?? leesHoeveelheid(fragment)?.getal ?? null;
    const eenheid = soort === 'schouw_crepi' ? 'M2' : post.eenheid;
    const prijsTekst = soort === 'schouw_crepi' ? 'aan de m2-prijs van de crepi' : `aan ${fmtEur(post.prijs)}${post.eenheid ? ` per ${post.eenheid.toLowerCase()}` : ''}`;
    const uitleg = `${q != null ? `${fmtGetal(q)} ${eenheid} ` : ''}${post.naam.charAt(0)}${post.naam.slice(1).toLowerCase()} ${prijsTekst}${q == null ? ` — hoeveel ${eenheid === 'DAG' ? 'dagen' : eenheid.toLowerCase()}?` : ''}`;
    out.push({ id: `ei_${hash(sleutel)}`, fragment, sleutel, soort, post, hoeveelheid: q, eenheid, uitleg, regelId: regel?.beslissing === 'post' ? regel.id : null });
  }
  return out;
}

/** Sleutels van de fragmenten die al als post op de calculatie staan. */
export function afgehandeldeFragmenten(matchedLines: MatchedLine[], meerwerkLines: MeerwerkLine[] = []): Set<string> {
  const out = new Set<string>();
  for (const l of [...matchedLines, ...meerwerkLines]) if (l.extraInformatie?.fragment) out.add(sleutelVan(l.extraInformatie.fragment));
  return out;
}

/**
 * Maakt van een bevestigde herkenning een post op de calculatie. Een losse schouw van meer dan 8 m2 wordt
 * crepi-meerwerk: de ladder geeft hem dezelfde m2-prijs als de crepi (9).
 */
export function postUitExtraInfo(
  v: ExtraInfoVraag,
  hoeveelheid: number,
  meta: { door?: string | null; op?: string | null } = {},
): { werkpost?: MatchedLine; meerwerk?: MeerwerkLine } {
  const post = v.post!;
  const bron = { fragment: v.fragment, regelId: v.regelId, beslistDoor: meta.door ?? null, beslistOp: meta.op ?? null };
  const id = v.id.replace(/^ei_/, '');
  if (v.soort === 'schouw_crepi') {
    return {
      meerwerk: {
        id: `mw_ei_${id}`, pdfNaam: `Losse schouw in crepi: ${v.fragment}`, naamArbeid: 'PLAATSEN VAN CREPI', categorie: 'EXTRA INFORMATIE',
        eenheid: 'M2', hoeveelheid, prijs: 0, subtotaal: 0, parsedItemId: `ei_${id}`, extraInformatie: bron,
      },
    };
  }
  const subtotaal = round2(hoeveelheid * post.prijs);
  return {
    werkpost: {
      id: `ml_ei_${id}`,
      naamArbeid: post.naam,
      eenheid: post.eenheid,
      prijs: post.prijs,
      hoeveelheid,
      subtotaal,
      consumedParsedItemIds: [],
      autoMatched: !!v.regelId,
      ...(post.gevel ? { gevelAccessoire: post.code } : {}),
      subItems: [{
        parsedItemId: `ei_${id}`, pdfNaam: v.fragment, plItemId: post.plItemId ?? (post.gevel ? `gevel:${post.code}` : post.code),
        hoeveelheid, eenheid: post.eenheid, prijs: post.prijs, subtotaal, confidence: 1, autoMatched: !!v.regelId, matchSource: 'manual-add',
      }],
      extraInformatie: bron,
    },
  };
}

// === Afwijkende eenheid (SPEC_calculatie 8.1) ===

/** Sleutel van een eenheidsregel: de post en de eenheid zoals ze op de fiche stond. */
export function eenheidSleutel(line: Pick<MatchedLine, 'naamArbeid' | 'gevelAccessoire'>, ficheEenheid: string): string {
  return `${line.gevelAccessoire ?? normalizeText(line.naamArbeid)}|${normEenheid(ficheEenheid)}`;
}

/**
 * Markeert werkposten waarvan de eenheid op de fiche afwijkt van de prijslijst ("2 stuks deurdorpels"
 * tegen een prijs per lopende meter). Zo'n post telt pas mee na bevestiging van de hoeveelheid; een
 * geleerde regel "zelfde getal" beantwoordt de vraag automatisch. Posten van de ladder, zonder eenheid of
 * met een bestaande vraag blijven onaangeroerd.
 */
export function markeerEenheidVragen(
  matchedLines: MatchedLine[],
  parsedItems: ParsedItem[],
  regels: ExtraInfoRegel[] = [],
): MatchedLine[] {
  const byId = new Map(parsedItems.map((p) => [p.id, p] as const));
  const regelPer = new Map(regels.filter((r) => r.actief && r.beslissing === 'zelfde_getal').map((r) => [r.sleutel, r] as const));
  return matchedLines.map((l) => {
    if (l.eenheidVraag || gevelRol(l.naamArbeid) || l.schijven || l.extraInformatie) return l;
    const prijslijst = normEenheid(l.eenheid);
    if (!prijslijst || prijslijst === 'FORFAIT') return l;
    const fiche = l.subItems
      .map((s) => normEenheid(byId.get(s.parsedItemId)?.eenheid))
      .find((e) => e && e !== 'FORFAIT' && e !== prijslijst);
    if (!fiche) return l;
    const regel = regelPer.get(eenheidSleutel(l, fiche));
    return {
      ...l,
      eenheidVraag: { ficheEenheid: fiche, prijslijstEenheid: prijslijst, ficheHoeveelheid: l.hoeveelheid, status: regel ? 'bevestigd' : 'open', regelId: regel?.id ?? null },
    };
  });
}

/** Zet de bevestigde hoeveelheid op een werkpost met een eenheidsvraag (verdeeld over de fiche-regels). */
export function bevestigEenheid(l: MatchedLine, hoeveelheid: number): MatchedLine {
  if (!l.eenheidVraag) return l;
  const totaal = l.subItems.reduce((s, x) => s + (x.hoeveelheid || 0), 0);
  const factor = totaal > 0 ? hoeveelheid / totaal : 0;
  const subItems = l.subItems.map((s, i) => {
    const hv = l.subItems.length === 1 ? hoeveelheid : Math.round((s.hoeveelheid || 0) * factor * 10000) / 10000;
    return { ...s, hoeveelheid: i === 0 && totaal === 0 ? hoeveelheid : hv, eenheid: l.eenheid, subtotaal: round2((i === 0 && totaal === 0 ? hoeveelheid : hv) * (s.prijs || 0)) };
  });
  return {
    ...l,
    hoeveelheid,
    subItems,
    subtotaal: round2(subItems.reduce((s, x) => s + x.subtotaal, 0)),
    eenheidVraag: { ...l.eenheidVraag, status: 'bevestigd' },
  };
}
